import type { ElementType } from 'react';
import type { LucideIcon } from '../icons/Icon';

/**
 * A single global-navigation destination. Shared by the desktop {@link NavRail} and the
 * mobile {@link BottomNav} so both render the same model. Router-agnostic: pass a
 * `linkComponent` (e.g. TanStack Router's `Link`) to the container and each item renders
 * as that component with `to`; without one it degrades to a plain `<a href>`.
 */
export interface NavModelItem {
  /** Stable identity + React key. */
  key: string;
  /** Accessible name and visible label (kept identical across collapsed/expanded so
   *  automation and screen readers see a stable name). */
  label: string;
  icon: LucideIcon;
  /** Destination path (passed to the link component as `to`, or used as `href`). */
  to: string;
  /** Optional count badge (e.g. pending items). */
  badge?: number;
  'data-testid'?: string;
}

/** The element used to render a navigable item — a router `Link` or a plain anchor. */
export type LinkComponent = ElementType;

/** Whether `currentPath` is within an item's section (prefix match, boundary-safe). */
export function isActivePath(currentPath: string, to: string): boolean {
  if (currentPath === to) return true;
  return currentPath.startsWith(to.endsWith('/') ? to : `${to}/`);
}
